import { ParseHelper } from 'src/app/modules/shared/parse.helper';
import { PercentageSymbolPipe } from './../../../modules/shared/pipes/percentage-symbol.pipe';
import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { DashboardService } from '../../../profile/dashboard/services/dashboard.service';
import { FormErrorHandler } from 'src/app/modules/shared/helpers/form-error-handler.helper';
import { ReportsAllFiltersApplyModes, ReportsAllFiltersAppyModels, typeReportsAllFiltersApplyModes } from 'src/app/reports/models/reports-all-filter-appy.models';
import { ReportService } from 'src/app/reports/reportService';
import { FINANCE_PROFITLOSS_DATE_RANGE_LIST } from '../../../modules/shared/constants/date-range-list.constants';
import { scrollToError } from '../../../modules/shared/helpers/scroll-to-error.helper';
import { DateRangeFilterService } from '../../../modules/shared/services/date-range-filter.service';
import { TimeZoneService } from '../../../modules/shared/services/timezone.service';
import { CurrencySymbolPipe } from 'src/app/modules/shared/pipes/currency-symbol.pipe';
import { FinancialAccountTypeNumber } from 'src/app/finance/constants/constants';

@Component({
    selector: 'app-dashboard-finance-reports',
    templateUrl: './dashboard-finance-reports.component.html',
    styleUrls: ['./dashboard-finance-reports.component.scss'],
    providers: [DatePipe, CurrencySymbolPipe, PercentageSymbolPipe]
})

export class DashboardFinanceReportsComponent implements OnInit, OnChanges, OnDestroy {

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    @Input() selectedReportFilters: any = null;
    @Input() reloadContent: boolean = false;
    @Output() onReloadContent: EventEmitter<any> = new EventEmitter<any>();
    @Output() onLastReloadTime: EventEmitter<any> = new EventEmitter<Date>();
    @Input() selectedWindow: string = '';
    @Input() displayFilterPopup: boolean = false;
    @Output() onCloseFilterPopup: EventEmitter<any> = new EventEmitter<any>();
    @Output() onUpdateAllFilters: EventEmitter<ReportsAllFiltersAppyModels> = new EventEmitter<ReportsAllFiltersAppyModels>();

    asOfDateReports: string[] = ['accountsPayable', 'accountsReceivable'];
    filterRangeReports: string[] = ['financeGrossProfit', 'financeGrossSales', 'financeCOGS', 'financeGrossMargin', 'financeNetIncome', 'financeGrossRevenue'];
    financialAccountTypeNumber = FinancialAccountTypeNumber;
    filterDataByTime = FINANCE_PROFITLOSS_DATE_RANGE_LIST;
    filterRanges = [
        { id: 1, name: "Total Only", value: "TotalOnly" },
        { id: 2, name: "Daily", value: "Days" },
        { id: 3, name: "Weekly", value: "Weeks" },
        { id: 4, name: "Monthly", value: "Months" },
        { id: 5, name: "Yearly", value: "Years" },
    ];
    reportsAllFiltersApplyModes = ReportsAllFiltersApplyModes;
    reportName: string = "";
    filterPopupHeader: string = "Filter";
    savedReportFilters: any = null;
    filterForm: FormGroup = new FormGroup({});
    isUnsavedData: boolean = false;
    detailsFormErrors: any = {};
    formErrorHandler: FormErrorHandler = new FormErrorHandler();

    //Report
    loadingReport: boolean = false;
    reportResultValue: number = 0;
    reportIcon: string = "pi pi-chart-line";

    //Finance Chart
    chartLabel: string = "Amount";
    chartBottomLabel: string = "Period";
    barChartbasicOptions: any;
    barChartData: any = null;

    profitReport: any[] = [];
    cols = [];
    subCols = [];
    income: any[] = [];
    cogs: any[] = [];
    expense: any[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private timezoneService: TimeZoneService,
        private datePipe: DatePipe,
        private reportService: ReportService,
        private dashboardService: DashboardService,
        private dateRangeFilterService: DateRangeFilterService,
        private currencySymbolPipe: CurrencySymbolPipe,
        private percentageSymbolPipe: PercentageSymbolPipe
    ) { }

    ngOnInit(): void {
        this.chartLabel = `Amount (${this.currencySymbolPipe.transform('')})`;
    }


    ngOnChanges(changes: SimpleChanges) {

        if (changes?.selectedWindow && changes.selectedWindow?.currentValue) {
            this.selectedWindow = changes.selectedWindow.currentValue;
        }

        if (changes?.selectedReportFilters && changes.selectedReportFilters?.currentValue) {
            this.selectedReportFilters = changes.selectedReportFilters?.currentValue || false;
            this.savedReportFilters = ParseHelper.DeepCopy(this.selectedReportFilters);
            this.setFilterDetails(true);
        }

        if (changes?.displayFilterPopup) {
            this.displayFilterPopup = changes.displayFilterPopup?.currentValue || false;
            if (this.displayFilterPopup) {
                this.setFilterDetails();
            }
        }

        if (changes?.reloadContent && changes?.reloadContent?.currentValue) {
            this.reloadContent = true;
            this.selectedReportFilters = ParseHelper.DeepCopy(this.savedReportFilters);
            this.setFilterDetails(true);
        }
    }

    setFilterDetails(reloadReport: boolean = false) {
        this.initFieldForm();
        const selectedDateFilter = this.filterDataByTime.find(d => d.code === this.selectedReportFilters.dateFilter);
        const filterRange = this.filterRanges.find(d => d.value === this.selectedReportFilters?.filterRange) || this.filterRanges[0];
        this.reportName = this.selectedReportFilters?.summaryWidgetTitle;
        this.filterForm.patchValue({
            selectedDateFilter: selectedDateFilter,
            filterRange: filterRange,
            dateFrom: this.selectedReportFilters?.fromDate ? new Date(this.selectedReportFilters?.fromDate) : null,
            dateTo: this.selectedReportFilters?.toDate ? new Date(this.selectedReportFilters?.toDate) : null
        });
        this.changeRange({ value: selectedDateFilter });

        let periodTitle = "";

        if (selectedDateFilter?.code == 'custom') {
            periodTitle = `${this.datePipe.transform(this.filterForm.getRawValue().dateFrom, 'MMM d, yy')} - ${this.datePipe.transform(this.filterForm.getRawValue().dateTo, 'MMM d, yy')}`;
        } else if (selectedDateFilter?.label) {
            periodTitle = (this.asOfDateReports.includes(this.selectedWindow)) ? `As Of ${selectedDateFilter?.label}` : selectedDateFilter?.label;
        }

        if (reloadReport) {
            this.barChartData = null;
            this.profitReport = [];
            this.chartLabel = `Amount (${this.currencySymbolPipe.transform('')})`;

            const globalThis = this;
            this.barChartbasicOptions = {
                tooltips: {
                    mode: 'index',
                    intersect: false
                },
                responsive: true,
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                let label = context.dataset.label || '';

                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += globalThis.currencySymbolPipe.transform(context.parsed.y, 'number2dp');
                                }
                                return label;
                            }
                        }
                    },
                    legend: {
                        labels: {
                            color: '#495057'
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: true,
                        ticks: {
                            color: '#495057'
                        },
                        grid: {
                            color: '#ebedef'
                        }
                    },
                    y: {
                        stacked: true,
                        ticks: {
                            color: '#495057'
                        },
                        grid: {
                            color: '#ebedef'
                        }
                    }
                }
            };

            if (this.selectedWindow == 'accountsReceivable') {
                this.getAccountsReceivable();
            } else if (this.selectedWindow == 'accountsPayable') {
                this.getAccountsPayable();
            } else if (this.selectedWindow) {
                if (this.filterForm.value.filterRange?.name) {
                    periodTitle = `${periodTitle} (${this.filterForm.value.filterRange?.name})`;
                }
                this.getAllProfitLossReport();
            }

            this.chartBottomLabel = periodTitle;
        }
    }

    initFieldForm() {
        this.detailsFormErrors = {};
        this.isUnsavedData = false;
        this.filterForm = this.formBuilder.group({
            selectedDateFilter: [null, [Validators.required]],
            filterRange: [null],
            dateFrom: [{value: null, disabled: true}, [Validators.required]],
            dateTo: [{value: null, disabled: true}]
        });

        this.formErrorHandler.handleErrors(this.filterForm, this.detailsFormErrors, {
            selectedDateFilter: {
                required: "Select period",
            },
            filterRange: {
                required: "Select display by",
            }
        });
    }


    changeRange($event): void {
        const selectedDateFilter = $event.value;
        let dateFrom = null;
        let dateTo = null;

        let data = {
            code: selectedDateFilter.code,
            fromDate: this.filterForm.getRawValue().dateFrom,
            toDate: this.filterForm.getRawValue().dateTo
        };
        const dateRange = this.dateRangeFilterService.changeRange(data);
        dateFrom = dateRange?.fromDate;
        dateTo = dateRange?.toDate;

        this.filterForm.patchValue({
            dateFrom: dateFrom,
            dateTo: dateTo
        });

        const dateFromCntl = this.filterForm.get('dateFrom');
        const dateToCntl = this.filterForm.get('dateTo');

        if(selectedDateFilter.code=='custom'){
            dateFromCntl?.enable();
            dateToCntl?.enable();
        }else{
            dateFromCntl?.disable();
            dateToCntl?.disable();
        }
    }

    cancelFilterPopup() {
        this.displayFilterPopup = false;
        this.onCloseFilterPopup.emit();
    }

    applyAllFilters(applyMode: typeReportsAllFiltersApplyModes) {

        if (this.filterForm.invalid) {
            this.formErrorHandler.showErrorOnSubmit();
            scrollToError();
            return; //Return if form invalid
        }

        const formValue = this.filterForm.getRawValue();

        let allFiltersValue: ReportsAllFiltersAppyModels = {
            applyMode: applyMode,
            dateFilter: {
                label: formValue.selectedDateFilter?.label,
                code: formValue.selectedDateFilter?.code,
                fromDate: formValue.dateFrom,
                toDate: formValue.dateTo
            },
            filterRange: formValue.filterRange?.value || null,
        }

        this.selectedReportFilters = {
            ...this.selectedReportFilters,
            ...allFiltersValue,
            ...allFiltersValue.dateFilter,
            dateFilter: allFiltersValue.dateFilter.code
        }
        if (applyMode == ReportsAllFiltersApplyModes.applySave) {
            this.savedReportFilters = ParseHelper.DeepCopy(this.selectedReportFilters);
        }
        this.setFilterDetails(true);

        this.onUpdateAllFilters.emit(allFiltersValue);
        this.cancelFilterPopup();
    }

    getAllProfitLossReport() {
        this.loadingReport = true;
        this.reportService
            .allProfitLossReportCustomize("profitandloss", this.formatDate2(this.filterForm.getRawValue().dateFrom), this.formatDate(this.filterForm.getRawValue().dateTo), this.filterForm.value.filterRange?.value)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                (res) => {
                    this.onReloadContent.emit(false);
                    this.loadingReport = false;
                    this.reportResultValue = 0;
                    this.profitReport = res;
                    this.generatePNLreportChart();
                },
                (err) => {
                    this.onReloadContent.emit(false);
                    this.loadingReport = false;
                }
            );
    }

    getAccountsReceivable() {
        this.loadingReport = true;
        const dateFilterType = this.filterForm.value.selectedDateFilter?.code == 'custom' ? 'Custom' : 'AsOfDate';
        this.dashboardService
            .getFinanceAccountReceivable(this.formatDate2(this.filterForm.getRawValue().dateFrom), this.formatDate(this.filterForm.getRawValue().dateTo), dateFilterType)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                (res) => {
                    this.onReloadContent.emit(false);
                    this.loadingReport = false;
                    const totalAmount = res?.length > 0 ? res.reduce((sum, invoice) => sum + invoice.totalDueAmount, 0) : 0;
                    this.reportResultValue = totalAmount;
                    this.generateARBarChart(res, '#7500FF');
                },
                (err) => {
                    this.onReloadContent.emit(false);
                    this.loadingReport = false;
                }
            );
    }

    getAccountsPayable() {
        this.loadingReport = true;
        const dateFilterType = this.filterForm.value.selectedDateFilter?.code == 'custom' ? 'Custom' : 'AsOfDate';
        this.dashboardService
            .getFinanceAccountPayable(this.formatDate2(this.filterForm.getRawValue().dateFrom), this.formatDate(this.filterForm.getRawValue().dateTo), dateFilterType)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                (res) => {
                    this.onReloadContent.emit(false);
                    this.loadingReport = false;
                    const totalAmount = res?.length > 0 ? res.reduce((sum, invoice) => sum + invoice.totaldueamount, 0) : 0;
                    this.reportResultValue = totalAmount;
                    this.generateARBarChart(res, '#66885F');
                },
                (err) => {
                    this.onReloadContent.emit(false);
                    this.loadingReport = false;
                }
            );
    }

    generateARBarChart(reportsData: any[], backgroundColor: string = '#42A5F5') {
        const stageChart = {
            labels: [],
            datasets: [
                {
                    label: 'Amount',
                    backgroundColor: backgroundColor,
                    data: []
                },
            ]
        }
        this.barChartData = stageChart;
        if (!reportsData || !reportsData.length) {
            return;
        }

        // const agingGroup = this.groupByData(reportsData,'pas_due_days_group');
        const agingRange = [
            { start: 0, end: 0 },
            { start: 1, end: 30 },
            { start: 31, end: 60 },
            { start: 61, end: 90 },
            { start: 90, end: 0 }
        ];

        agingRange.map((age, index) => {
            let dataList = [];
            let label: string = "Current";
            if (age.start == 0) {
                dataList = reportsData.filter(data => data?.aging <= age.start);
            } else if (age.end == 0) {
                dataList = reportsData.filter(data => data?.aging > age.start);
                label = `> ${age.start}`;
            } else {
                dataList = reportsData.filter(data => data?.aging >= age.start && data?.aging <= age.end);
                label = `${age.start} - ${age.end}`;
            }

            stageChart.labels.push(label);
            const totalAmount = dataList?.length > 0 ? dataList.reduce((sum, invoice) => sum + (invoice?.totalDueAmount || invoice?.totaldueamount || 0), 0) : 0;
            stageChart.datasets[0].data.push(totalAmount);
        });
        this.barChartData = stageChart;
    }

    groupByData(xs, key) {
        const groupList = [];
        const group = xs.reduce(function (rv, x) {
            (rv[x[key]] = rv[x[key]] || []).push(x);
            return rv;
        }, {});

        Object.keys(group).map(k => {
            let obj = {};
            obj[key] = k;
            obj['dataList'] = group[k];

            groupList.push(obj);
        });

        return groupList;
    };

    private formatDate(date: Date): any {
        date = date ? date : new Date();
        let utcDate = this.datePipe.transform(this.timezoneService.saveDateTimeWithZone(date), "MM-dd-yyyy");
        return utcDate;
    }

    private formatDate2(date: Date): any {
        let utcDate = this.datePipe.transform(date, "MM-dd-yyyy");
        return utcDate;
    }

    //#region ***P & L reports data***
    generatePNLreportChart() {
        this.changeCols();
        let reportsData: any[] = [];
        let backgroundColor: string = '#42A5F5';
        switch (this.selectedWindow) {
            case 'financeGrossProfit':
                reportsData = this.getGross(this.profitReport);
                backgroundColor = '#00C3A4';
                break;
            case 'financeNetIncome':
                reportsData = this.getNetIncome(this.profitReport);
                backgroundColor = '#42A5F5';
                break;
            // case 'financeGrossRevenue':
            //     reportsData = this.getGrossRevenue(this.profitReport);
            //     backgroundColor = '#FFE5C9';
            //     break;
        }

        if (this.selectedWindow == 'financeCOGS') {
            this.generateCOGSChart(this.profitReport);
            return;
        } else if (this.selectedWindow == 'financeGrossRevenue') {
            this.generateGrossRevenue(this.profitReport);
            return;
        } else if (this.selectedWindow == 'financeGrossMargin') {
            this.generateGrossMargin(this.profitReport);
            return;
        }

        const stageChart = {
            labels: [],
            datasets: [
                {
                    label: 'Amount',
                    backgroundColor: backgroundColor,
                    data: []
                },
            ]
        }
        this.barChartData = stageChart;
        if (!reportsData || !reportsData.length) {
            return;
        }

        this.cols.map((col, index) => {
            stageChart.labels.push(col.header);
            const totalAmount = reportsData?.[index] || 0;
            stageChart.datasets[0].data.push(totalAmount);
        });
        this.barChartData = stageChart;
    }

    changeCols() {
        if (this.filterForm.value.filterRange?.value == 'TotalOnly') {
            this.cols = [
                { header: `${this.datePipe.transform(this.filterForm.getRawValue().dateFrom, 'MMM d, yy')} - ${this.datePipe.transform(this.filterForm.getRawValue().dateTo, 'MMM d, yy')}`, field: "balance" }
            ];
            this.subCols = [];
        } else if (this.filterForm.value.filterRange?.value == 'Days') {
            this.formatDays();
        } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
            this.formatWeeks();
        } else if (this.filterForm.value.filterRange?.value == 'Months') {
            this.formatMonths();
        } else if (this.filterForm.value.filterRange?.value == 'Years') {
            this.formatYears();
        }
    }

    formatDays() {
        let dates = this.getDaysArray(this.filterForm.getRawValue().dateFrom, this.filterForm.getRawValue().dateTo);
        this.cols = [];
        for (let d of dates) {
            let eachCol = {
                header: this.datePipe.transform(d, 'MMM d, yy'),
                field: "childbalance",
                date: d
            }
            this.cols.push(eachCol)
        }
        this.subCols = JSON.parse(JSON.stringify(this.cols));
    }

    formatWeeks() {
        this.cols = [];
        let start = new Date(this.filterForm.getRawValue().dateFrom);
        let end = new Date(this.filterForm.getRawValue().dateTo);
        let sDate;
        let ssDate;
        let eDate;
        let dateArr = [];
        while (start <= end) {
            let sDate = new Date(start);
            let eDate = new Date(start);
            eDate.setDate(eDate.getDate() + 6);
            if (eDate > end) {
                eDate = new Date(end);
            }
            dateArr.push({ 'startDate': sDate, 'endDate': eDate });
            start.setDate(start.getDate() + 7);
        }

        for (let date of dateArr) {
            let header = this.datePipe.transform(date.startDate, 'MM/dd/yyyy') + "-" + this.datePipe.transform(date.endDate, 'MM/dd/yyyy')
            this.cols.push({ header: header, field: "childbalance", date: date },)
        }
        this.subCols = JSON.parse(JSON.stringify(this.cols));
    }

    formatMonths() {
        const fromYear = this.filterForm.getRawValue().dateFrom.getFullYear();
        const fromMonth = this.filterForm.getRawValue().dateFrom.getMonth();
        const toYear = this.filterForm.getRawValue().dateTo.getFullYear();
        const toMonth = this.filterForm.getRawValue().dateTo.getMonth();
        const months = [];
        this.cols = []

        for (let year = fromYear; year <= toYear; year++) {
            let monthNum = year === fromYear ? fromMonth : 0;
            const monthLimit = year === toYear ? toMonth : 11;

            for (; monthNum <= monthLimit; monthNum++) {
                let month = monthNum + 1;
                months.push({ year, month });
            }
        }
        for (let m of months) {
            let header = m.month < 10 ? '0' + m.month + "/" + m.year : m.month + "/" + m.year;
            this.cols.push({ header: header, field: "childbalance", date: m },)
        }
        this.subCols = JSON.parse(JSON.stringify(this.cols));
    }

    formatYears() {
        let fromYear = this.filterForm.getRawValue().dateFrom.getFullYear();
        let toYear = this.filterForm.getRawValue().dateTo.getFullYear();
        let years = [];
        this.cols = [];
        for (let year = fromYear; year <= toYear; year++) {
            years.push({ year });
        }
        for (let m of years) {
            let header = m.year;
            this.cols.push({ header: header, field: "childbalance", date: m },)
        }
        this.subCols = JSON.parse(JSON.stringify(this.cols));
    }

    getDaysArray(start, end) {
        for (var arr = [], dt = new Date(start); dt <= new Date(end); dt.setDate(dt.getDate() + 1)) {
            arr.push(new Date(dt));
        }
        return arr;
    };

    getDayBalance(child) {
        let arr1 = [];
        let arr2 = [];
        let arr3 = [];

        for (let c of child.childAccounts) {
            arr1.push(c.periodStartDate)
            arr2.push(c)
        }
        for (let col of this.subCols) {
            if (this.isInArray(arr1, col.date)) {
                let data = arr2.find(item => this.sameDay(new Date(item.periodStartDate), new Date(col.date)))
                arr3.push(data.balance)
            } else {
                arr3.push("0");
            }
        }
        return arr3;
    }

    getWeekBalance(child) {
        let start = [];
        let end = [];
        let arr2 = [];
        let arr3 = [];

        for (let c of child.childAccounts) {
            start.push(c.periodStartDate)
            end.push(c.periodEndDate)
            arr2.push(c);
        }
        this.subCols.forEach((col, index) => {
            if (index == this.subCols.length - 1) {
                if (this.isInArray(start, col.date?.startDate) && new Date(col.date?.endDate).getTime() <= new Date(end[end.length - 1]).getTime()) {
                    let data = arr2.find(item => this.sameDay(new Date(item.periodStartDate), new Date(col.date.startDate)))
                    arr3.push(data.balance)
                }
            } else {
                if (this.isInArray(start, col.date?.startDate) && this.isInArray(end, col.date?.endDate)) {
                    let data = arr2.find(item => this.sameDay(new Date(item.periodStartDate), new Date(col.date.startDate)))
                    arr3.push(data.balance)
                } else {
                    arr3.push("0");
                }
            }
        });
        return arr3;
    }

    getMonthBalance(child) {
        let arr1 = [];
        let arr2 = [];
        let arr3 = [];

        for (let c of child.childAccounts) {
            arr1.push(new Date(c.periodStartDate).getMonth() + 1);
            arr2.push(c)
        }
        for (let col of this.subCols) {
            if (this.isInArray2(arr1, col.date?.month)) {
                let data = arr2.find(item => new Date(item.periodStartDate).getMonth() + 1 == col.date.month)
                arr3.push(data.balance)
            } else {
                arr3.push("0");
            }
        }
        return arr3;
    }

    getYearBalance(child) {
        let arr1 = [];
        let arr2 = [];
        let arr3 = [];

        for (let c of child.childAccounts) {
            arr1.push(new Date(c.periodStartDate).getFullYear());
            arr2.push(c)
        }
        for (let col of this.subCols) {
            if (this.isInArray2(arr1, col.date?.year)) {
                let data = arr2.find(item => new Date(item.periodStartDate).getFullYear() == col.date.year)
                arr3.push(data.balance)
            } else {
                arr3.push("0");
            }
        }
        return arr3;
    }

    isInArray(array, value) {
        return (array.find(item => { return this.sameDay(new Date(item), new Date(value)) }) || []).length > 0;
    }

    isInArray2(array, value) {
        return array.some(item => item == value)
    }

    sameDay(d1, d2) {
        return d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();
    }

    getGross(data) {
        let arr = [];
        let income = [];
        let cogs = [];
        let income2 = [];
        let cogs2 = [];

        data.forEach(e => {
            for (let a of e.childAccounts) {
                if (a.financialAccountTypeNumber == this.financialAccountTypeNumber.Income) {
                    if (this.filterForm.value.filterRange?.value == 'Days') {
                        this.getDayBalance(a).forEach((b, index) => {
                            this.income = [];
                            income.push({ balance: b, index });
                            income2 = income.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            income2.forEach(ele => {
                                this.income.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
                        this.getWeekBalance(a).forEach((b, index) => {
                            this.income = [];
                            income.push({ balance: b, index });
                            income2 = income.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            income2.forEach(ele => {
                                this.income.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Months') {
                        this.getMonthBalance(a).forEach((b, index) => {
                            this.income = [];
                            income.push({ balance: b, index });
                            income2 = income.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            income2.forEach(ele => {
                                this.income.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Years') {
                        this.getYearBalance(a).forEach((b, index) => {
                            this.income = [];
                            income.push({ balance: b, index });
                            income2 = income.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            income2.forEach(ele => {
                                this.income.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    }

                } else if (a.financialAccountTypeNumber == this.financialAccountTypeNumber.COGS) {
                    if (this.filterForm.value.filterRange?.value == 'Days') {
                        this.getDayBalance(a).forEach((b, index) => {
                            this.cogs = [];
                            cogs.push({ balance: b, index });
                            cogs2 = cogs.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            cogs2.forEach(ele => {
                                this.cogs.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
                        this.getWeekBalance(a).forEach((b, index) => {
                            this.cogs = [];
                            cogs.push({ balance: b, index });
                            cogs2 = cogs.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            cogs2.forEach(ele => {
                                this.cogs.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Months') {
                        this.getMonthBalance(a).forEach((b, index) => {
                            this.cogs = [];
                            cogs.push({ balance: b, index });
                            cogs2 = cogs.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            cogs2.forEach(ele => {
                                this.cogs.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Years') {
                        this.getYearBalance(a).forEach((b, index) => {
                            this.cogs = [];
                            cogs.push({ balance: b, index });
                            cogs2 = cogs.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            cogs2.forEach(ele => {
                                this.cogs.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    }

                }
            }
        })
        this.income.forEach((a, index) => {
            arr.push(Number(a) - Number(this.cogs[index]));
        })

        if (this.filterForm.value.filterRange?.value == 'TotalOnly') {
            return [this.getTotalGross(this.profitReport)];
        } else {
            return arr;
        }
    }

    getTotalGross(data) {
        let income;
        let cogs;
        for (let s of data) {
            if (s.financialAccountTypeNumber == this.financialAccountTypeNumber.Income) {
                income = s.balance;
            } else if (s.financialAccountTypeNumber == this.financialAccountTypeNumber.COGS) {
                cogs = s.balance;
            }
        }
        return Number(income) - Number(cogs);
    }

    getNetIncome(data) {
        let gross = [];
        let net = [];
        let income = [];
        let cogs = [];
        let expense = [];
        let income2 = [];
        let cogs2 = [];
        let expense2 = [];

        data.forEach(e => {
            for (let a of e.childAccounts) {
                if (a.financialAccountTypeNumber == this.financialAccountTypeNumber.Income) {
                    if (this.filterForm.value.filterRange?.value == 'Days') {
                        this.getDayBalance(a).forEach((b, index) => {
                            this.income = [];
                            income.push({ balance: b, index });
                            income2 = income.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            income2.forEach(ele => {
                                this.income.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
                        this.getWeekBalance(a).forEach((b, index) => {
                            this.income = [];
                            income.push({ balance: b, index });
                            income2 = income.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            income2.forEach(ele => {
                                this.income.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Months') {
                        this.getMonthBalance(a).forEach((b, index) => {
                            this.income = [];
                            income.push({ balance: b, index });
                            income2 = income.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            income2.forEach(ele => {
                                this.income.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Years') {
                        this.getYearBalance(a).forEach((b, index) => {
                            this.income = [];
                            income.push({ balance: b, index });
                            income2 = income.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            income2.forEach(ele => {
                                this.income.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    }

                } else if (a.financialAccountTypeNumber == this.financialAccountTypeNumber.COGS) {
                    if (this.filterForm.value.filterRange?.value == 'Days') {
                        this.getDayBalance(a).forEach((b, index) => {
                            this.cogs = [];
                            cogs.push({ balance: b, index });
                            cogs2 = cogs.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            cogs2.forEach(ele => {
                                this.cogs.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
                        this.getWeekBalance(a).forEach((b, index) => {
                            this.cogs = [];
                            cogs.push({ balance: b, index });
                            cogs2 = cogs.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            cogs2.forEach(ele => {
                                this.cogs.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Months') {
                        this.getMonthBalance(a).forEach((b, index) => {
                            this.cogs = [];
                            cogs.push({ balance: b, index });
                            cogs2 = cogs.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            cogs2.forEach(ele => {
                                this.cogs.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Years') {
                        this.getYearBalance(a).forEach((b, index) => {
                            this.cogs = [];
                            cogs.push({ balance: b, index });
                            cogs2 = cogs.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            cogs2.forEach(ele => {
                                this.cogs.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    }

                } else if (a.financialAccountTypeNumber == this.financialAccountTypeNumber.Expense) {
                    if (this.filterForm.value.filterRange?.value == 'Days') {
                        this.getDayBalance(a).forEach((b, index) => {
                            this.expense = [];
                            expense.push({ balance: b, index });
                            expense2 = expense.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            expense2.forEach(ele => {
                                this.expense.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
                        this.getWeekBalance(a).forEach((b, index) => {
                            this.expense = [];
                            expense.push({ balance: b, index });
                            expense2 = expense.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            expense2.forEach(ele => {
                                this.expense.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Months') {
                        this.getMonthBalance(a).forEach((b, index) => {
                            this.expense = [];
                            expense.push({ balance: b, index });
                            expense2 = expense.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            expense2.forEach(ele => {
                                this.expense.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    } else if (this.filterForm.value.filterRange?.value == 'Years') {
                        this.getYearBalance(a).forEach((b, index) => {
                            this.expense = [];
                            expense.push({ balance: b, index });
                            expense2 = expense.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
                            expense2.forEach(ele => {
                                this.expense.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
                            })
                        })
                    }
                }
            }
        })
        this.income.forEach((a, index) => {
            gross.push(Number(a) - Number(this.cogs[index]));
        });
        gross.forEach((a, index) => {
            net.push(Number(a) - Number(this.expense[index]));
        });

        if (this.filterForm.value.filterRange?.value == 'TotalOnly') {
            return [this.getTotalNetIncome(this.profitReport)];
        } else {
            return net;
        }
    }

    getTotalNetIncome(data) {
        let income;
        let cogs;
        let expense;
        for (let s of data) {
            if (s.financialAccountTypeNumber == this.financialAccountTypeNumber.Income) {
                income = s.balance;
            } else if (s.financialAccountTypeNumber == this.financialAccountTypeNumber.COGS) {
                cogs = s.balance;
            } else if (s.financialAccountTypeNumber == this.financialAccountTypeNumber.Expense) {
                expense = s.balance;
            }
        }
        return Number(income) - Number(cogs) - Number(expense);
    }

    getGrossRevenue(profitReport: any[]) {
        const grossRevenue = profitReport.find(pl => pl?.financialAccountTypeNumber == this.financialAccountTypeNumber.Income);
        return this.getTotal(grossRevenue);
    }

    generateGrossRevenue(profitReport: any[]) {
        const grossRevenue = profitReport.find(pl => pl?.financialAccountTypeNumber == this.financialAccountTypeNumber.Income);
        let arr1 = [];

        let accountsData: { financialAccountName: string, color: string, data: number[] }[] = [];
        const randomColors = this.randColor(grossRevenue.childAccounts.length);
        grossRevenue.childAccounts.map((a, index) => {
            if (this.filterForm.value.filterRange?.value == 'Days') {
                arr1 = this.getDayBalance(a);
            } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
                arr1 = this.getWeekBalance(a);
            } else if (this.filterForm.value.filterRange?.value == 'Months') {
                arr1 = this.getMonthBalance(a);
            } else if (this.filterForm.value.filterRange?.value == 'Years') {
                arr1 = this.getYearBalance(a);
            } else if (this.filterForm.value.filterRange?.value == 'TotalOnly') {
                arr1 = [a.balance];
            }

            accountsData.push({
                financialAccountName: a.financialAccountName,
                data: arr1,
                color: randomColors[index]
            })
        });

        const stageChart = {
            labels: [],
            datasets: []
        }
        this.barChartData = stageChart;
        if (!accountsData || !accountsData.length) {
            return;
        }

        this.cols.map((col, index) => {
            stageChart.labels.push(col.header);
        });

        accountsData.map(accountData => {
            const stackData = {
                type: 'bar',
                label: accountData.financialAccountName,
                backgroundColor: accountData.color,
                data: accountData.data
            }

            stageChart.datasets.push(stackData);
        });

        this.barChartData = stageChart;
    }

    generateCOGSChart(profitReport: any[]) {
        const COGS = profitReport.find(pl => pl?.financialAccountTypeNumber == this.financialAccountTypeNumber.COGS);
        let arr1 = [];

        let accountsData: { financialAccountName: string, color: string, data: number[] }[] = [];

        COGS.childAccounts.map((a, index) => {
            if (this.filterForm.value.filterRange?.value == 'Days') {
                arr1 = this.getDayBalance(a);
            } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
                arr1 = this.getWeekBalance(a);
            } else if (this.filterForm.value.filterRange?.value == 'Months') {
                arr1 = this.getMonthBalance(a);
            } else if (this.filterForm.value.filterRange?.value == 'Years') {
                arr1 = this.getYearBalance(a);
            } else if (this.filterForm.value.filterRange?.value == 'TotalOnly') {
                arr1 = [a.balance];
            }

            accountsData.push({
                financialAccountName: a.financialAccountName,
                data: arr1,
                color: this.getColor(index)
            })
        });

        const stageChart = {
            labels: [],
            datasets: []
        }
        this.barChartData = stageChart;
        if (!accountsData || !accountsData.length) {
            return;
        }

        this.cols.map((col, index) => {
            stageChart.labels.push(col.header);
        });

        accountsData.map(accountData => {
            const stackData = {
                type: 'bar',
                label: accountData.financialAccountName,
                backgroundColor: accountData.color,
                data: accountData.data
            }

            stageChart.datasets.push(stackData);
        });

        this.barChartData = stageChart;
    }

    generateGrossMargin(profitReport: any[]) {
        this.chartLabel = `Gross Margin (%)`;
        const globalThis = this;
        this.barChartbasicOptions = {
            tooltips: {
                mode: 'index',
                intersect: false
            },
            responsive: true,
            plugins: {
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            let label = context.dataset.label || '';

                            if (label) {
                                label += ': ';
                            }
                            if (context.parsed.y !== null) {
                                label += globalThis.percentageSymbolPipe.transform(context.parsed.y, 'number2dp');
                            }
                            return label;
                        }
                    }
                },
                legend: {
                    labels: {
                        color: '#495057'
                    }
                }
            },
            scales: {
                x: {
                    stacked: true,
                    ticks: {
                        color: '#495057'
                    },
                    grid: {
                        color: '#ebedef'
                    }
                },
                y: {
                    stacked: true,
                    ticks: {
                        color: '#495057'
                    },
                    grid: {
                        color: '#ebedef'
                    }
                }
            }
        };

        const grossProfit = this.getGross(profitReport);
        const grossRevenue = this.getGrossRevenue(profitReport);

        const stageChart = {
            labels: [],
            datasets: [
                {
                    label: 'Gross Margin',
                    backgroundColor: '#B20020',
                    data: []
                },
            ]
        }
        this.barChartData = stageChart;
        if (!grossProfit || !grossProfit.length || !grossRevenue || !grossRevenue.length) {
            return;
        }

        this.cols.map((col, index) => {
            stageChart.labels.push(col.header);
            const totalAmount = (grossProfit?.[index] && grossRevenue?.[index]) ? ((grossProfit?.[index] / grossRevenue?.[index]) * 100) : 0;
            stageChart.datasets[0].data.push(totalAmount);
        });
        this.barChartData = stageChart;
    }

    getTotal(pl) {
        let arr1 = [];
        let arr2 = [];
        let arr3 = [];

        for (let a of pl.childAccounts) {
            if (this.filterForm.value.filterRange?.value == 'Days') {
                this.getDayBalance(a).forEach((b, index) => {
                    arr1.push({ balance: b, index });
                })
            } else if (this.filterForm.value.filterRange?.value == 'Weeks') {
                this.getWeekBalance(a).forEach((b, index) => {
                    arr1.push({ balance: b, index });
                })
            } else if (this.filterForm.value.filterRange?.value == 'Months') {
                this.getMonthBalance(a).forEach((b, index) => {
                    arr1.push({ balance: b, index });
                })
            } else if (this.filterForm.value.filterRange?.value == 'Years') {
                this.getYearBalance(a).forEach((b, index) => {
                    arr1.push({ balance: b, index });
                })
            }
        }
        arr2 = arr1.reduce((a, v) => ((a[v.index] || (a[v.index] = [])).push(v), a), []);
        arr2.forEach(ele => {
            arr3.push(ele.map(item => item.balance).reduce((prev, next) => prev + next))
        })

        if (this.filterForm.value.filterRange?.value == 'TotalOnly') {
            return [pl.balance];
        } else {
            return arr3;
        }
    }

    getColor(index) {
        const colorList = ['#42A5F5', '#66BB6A', '#FFA726', '#7E57C2', '#26A69A', "#FF6384", "#36A2EB"];

        if (index > 6) {
            let i = index % 6;
            return colorList[i];
        } else {
            return colorList[index];
        }
    }

    randColor(numColors) {
        const chars = "ABCDEF0123456789";
        const size = chars.length;
        let str = [];

        for (let i = 0; i < numColors; i++) {
            str[i] = "#";
            for (let j = 0; j < 6; j++) {
                const rand = Math.floor(Math.random() * (size - 1)) + 1;
                str[i] += chars[rand];
            }
        }
        return str;
    }

    //#endregion

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }
}
